import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO, startOfWeek, endOfWeek, addWeeks, isSameWeek, isSameMonth, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isAfter, isBefore, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Calendar,
  Clock,
  Users,
  MapPin,
  ChevronRight,
  CheckCircle2,
  ArrowLeft,
  Phone,
  Mail,
  User,
  MessageSquare,
  Info,
  ChevronLeft,
  CalendarDays
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sources de communication
const COMMUNICATION_SOURCES = [
  { value: 'reseaux_sociaux', label: 'Réseaux sociaux (Facebook, Instagram...)' },
  { value: 'bouche_a_oreille', label: 'Bouche à oreille' },
  { value: 'radio', label: 'Radio' },
  { value: 'affiches', label: 'Affiches / Flyers' },
  { value: 'parrain', label: 'Parrain / Marraine' },
  { value: 'autre', label: 'Autre' }
];

const PortesOuvertes = () => {
  const [step, setStep] = useState('sessions'); // 'sessions', 'form', 'success'
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [golfInfo, setGolfInfo] = useState({ golf_name: 'Golf', golf_address: '' });
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' ou 'day'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentDate, setCurrentDate] = useState(new Date()); // Pour la vue jour
  const [portalSettings, setPortalSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  
  // Formulaire
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    age: '',
    sex: '',
    handedness: '',
    communication_source: '',
    communication_source_other: '',
    sponsor_name: '',
    comment: ''
  });

  // Regrouper les sessions par date
  const sessionsByDate = useMemo(() => {
    const grouped = {};
    sessions.forEach(session => {
      if (!session.datetime) return;
      const date = typeof session.datetime === 'string' ? parseISO(session.datetime) : new Date(session.datetime);
      const dateKey = format(date, 'yyyy-MM-dd');
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(session);
    });
    return grouped;
  }, [sessions]);

  // Jours scrollables pour la vue jour (basés sur les jours avec sessions)
  const scrollDays = useMemo(() => {
    const datesWithSessions = Object.keys(sessionsByDate).sort();
    if (datesWithSessions.length > 0) {
      return datesWithSessions.map(d => parseISO(d));
    }
    const today = new Date();
    return Array.from({ length: 14 }, (_, i) => addDays(today, i));
  }, [sessionsByDate]);

  // Sessions du jour actuel pour la vue jour
  const currentDaySessions = useMemo(() => {
    const dateKey = format(currentDate, 'yyyy-MM-dd');
    return (sessionsByDate[dateKey] || []).sort((a, b) => {
      const dateA = typeof a.datetime === 'string' ? parseISO(a.datetime) : new Date(a.datetime);
      const dateB = typeof b.datetime === 'string' ? parseISO(b.datetime) : new Date(b.datetime);
      return dateA - dateB;
    });
  }, [currentDate, sessionsByDate]);

  // Jours du mois actuel pour le calendrier
  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Ajouter les jours du mois précédent pour compléter la première semaine
    const firstDayOfWeek = startOfWeek(start, { locale: fr });
    const paddingStart = eachDayOfInterval({ start: firstDayOfWeek, end: start }).slice(0, -1);
    
    // Ajouter les jours du mois suivant pour compléter la dernière semaine
    const lastDayOfWeek = endOfWeek(end, { locale: fr });
    const paddingEnd = eachDayOfInterval({ start: end, end: lastDayOfWeek }).slice(1);
    
    return [...paddingStart, ...days, ...paddingEnd];
  }, [currentMonth]);

  useEffect(() => {
    checkStatus();
    loadPortalSettings();
  }, []);

  const loadPortalSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/client-portal/public-settings`);
      if (res.ok) {
        const data = await res.json();
        setPortalSettings(data);
      }
    } catch (e) {
      console.log('Settings load failed:', e);
    } finally {
      setSettingsLoading(false);
    }
  };

  const checkStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/portes-ouvertes/status`);
      const data = await res.json();
      
      if (!data.enabled) {
        setStep('closed');
        return;
      }
      
      setGolfInfo({
        golf_name: data.golf_name || 'Golf',
        golf_address: data.golf_address || '',
        info_pratiques: data.info_pratiques || ''
      });
      
      loadSessions();
    } catch (error) {
      console.error('Erreur:', error);
      setStep('closed');
    }
  };

  const loadSessions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/public/portes-ouvertes/sessions`);
      const data = await res.json();
      setSessions(data);
      
      // Positionner le calendrier sur la première session disponible
      if (data.length > 0) {
        const firstDt = data[0].datetime;
        const firstDate = typeof firstDt === 'string' ? parseISO(firstDt) : new Date(firstDt);
        setCurrentMonth(firstDate);
        setCurrentDate(firstDate);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement des sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSession = (session) => {
    if (session.is_full) {
      toast.error('Cette session est complète');
      return;
    }
    setSelectedSession(session);
    setStep('form');
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (!form.age || parseInt(form.age) < 5 || parseInt(form.age) > 99) {
      toast.error('Veuillez entrer un âge valide');
      return;
    }
    
    if (!form.sex || !form.handedness || !form.communication_source) {
      toast.error('Veuillez répondre à toutes les questions');
      return;
    }
    
    if (form.communication_source === 'parrain' && !form.sponsor_name) {
      toast.error('Veuillez indiquer le nom de votre parrain/marraine');
      return;
    }

    if (form.communication_source === 'autre' && !form.communication_source_other) {
      toast.error('Veuillez préciser comment vous nous avez connu');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/public/portes-ouvertes/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: selectedSession.id,
          ...form,
          age: parseInt(form.age)
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Erreur lors de l\'inscription');
      }
      
      setStep('success');
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatSessionDate = (datetime) => {
    if (!datetime) return '';
    const date = typeof datetime === 'string' ? parseISO(datetime) : new Date(datetime);
    return format(date, "EEEE d MMMM yyyy", { locale: fr });
  };

  const formatSessionTime = (datetime) => {
    if (!datetime) return '';
    const date = typeof datetime === 'string' ? parseISO(datetime) : new Date(datetime);
    return format(date, "HH:mm", { locale: fr });
  };

  const formatSessionEndTime = (datetime, durationMinutes) => {
    if (!datetime || !durationMinutes) return '';
    const date = typeof datetime === 'string' ? parseISO(datetime) : new Date(datetime);
    const endDate = new Date(date.getTime() + durationMinutes * 60000);
    return format(endDate, "HH:mm", { locale: fr });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h${mins.toString().padStart(2, '0')}` : `${hours}h`;
  };

  // Page fermée
  if (step === 'closed') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Info className="w-8 h-8 text-amber-600" />
            </div>
            <CardTitle className="text-xl">Inscriptions fermées</CardTitle>
            <CardDescription>
              Les inscriptions aux Portes Ouvertes ne sont pas ouvertes pour le moment.
              Revenez bientôt !
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Page de succès
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <CardTitle className="text-xl text-emerald-700">Inscription confirmée !</CardTitle>
            <CardDescription className="text-base mt-2">
              Vous êtes inscrit(e) à la session du{' '}
              <strong>{formatSessionDate(selectedSession?.datetime)}</strong> de{' '}
              <strong>{formatSessionTime(selectedSession?.datetime)}</strong> à{' '}
              <strong>{formatSessionEndTime(selectedSession?.datetime, selectedSession?.duration_minutes)}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-emerald-50 rounded-lg p-4 text-sm text-emerald-800">
              <p className="font-medium mb-2">📧 Un email de confirmation vous a été envoyé</p>
              <p>Pensez à vérifier vos spams si vous ne le trouvez pas.</p>
            </div>
            
            <div className="bg-neutral-50 rounded-lg p-4 text-sm text-left">
              <p className="font-medium mb-2">📋 Ce qu'il faut savoir :</p>
              <ul className="space-y-1 text-neutral-600">
                <li>• Arrivez 10 minutes avant le début</li>
                <li>• Aucun équipement nécessaire</li>
                <li>• Tenue décontractée acceptée</li>
              </ul>
            </div>
            
            <p className="text-sm text-neutral-500">
              À très bientôt au {golfInfo.golf_name} ! 🏌️
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Formulaire d'inscription
  if (step === 'form') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white py-4 px-3">
        <div className="max-w-lg mx-auto">
          {/* Header compact */}
          <button
            onClick={() => setStep('sessions')}
            className="flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900 mb-3"
          >
            <ArrowLeft size={16} />
            Retour
          </button>
          
          <Card>
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-base">Inscription Portes Ouvertes</CardTitle>
              <CardDescription className="text-xs">
                <strong>{formatSessionDate(selectedSession?.datetime)}</strong> de <strong>{formatSessionTime(selectedSession?.datetime)}</strong> à <strong>{formatSessionEndTime(selectedSession?.datetime, selectedSession?.duration_minutes)}</strong>
              </CardDescription>
            </CardHeader>
            
            <CardContent className="px-4 pb-4">
              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Nom / Prénom */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="first_name" className="text-xs">Prénom *</Label>
                    <Input
                      id="first_name"
                      value={form.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      placeholder="Jean"
                      className="h-9 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="text-xs">Nom *</Label>
                    <Input
                      id="last_name"
                      value={form.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      placeholder="Dupont"
                      className="h-9 text-sm"
                      required
                    />
                  </div>
                </div>
                
                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-xs">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="jean.dupont@email.com"
                    className="h-9 text-sm"
                    required
                  />
                </div>
                
                {/* Téléphone */}
                <div>
                  <Label htmlFor="phone" className="text-xs">Téléphone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="06 12 34 56 78"
                    className="h-9 text-sm"
                    required
                  />
                </div>
                
                {/* Age / Sexe / Main sur une ligne */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label htmlFor="age" className="text-xs">Âge *</Label>
                    <Input
                      id="age"
                      type="number"
                      min="5"
                      max="99"
                      value={form.age}
                      onChange={(e) => handleInputChange('age', e.target.value)}
                      placeholder="35"
                      className="h-9 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Sexe *</Label>
                    <Select value={form.sex} onValueChange={(v) => handleInputChange('sex', v)}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder="..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="homme">Homme</SelectItem>
                        <SelectItem value="femme">Femme</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Main *</Label>
                    <Select value={form.handedness} onValueChange={(v) => handleInputChange('handedness', v)}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder="..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="droitier">Droitier</SelectItem>
                        <SelectItem value="gaucher">Gaucher</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Source */}
                <div>
                  <Label className="text-xs">Comment nous avez-vous connu ? *</Label>
                  <Select 
                    value={form.communication_source} 
                    onValueChange={(v) => handleInputChange('communication_source', v)}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Sélectionnez..." />
                    </SelectTrigger>
                    <SelectContent>
                      {COMMUNICATION_SOURCES.map(source => (
                        <SelectItem key={source.value} value={source.value}>
                          {source.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {form.communication_source === 'parrain' && (
                  <div>
                    <Label htmlFor="sponsor_name" className="text-xs">Nom du parrain/marraine *</Label>
                    <Input
                      id="sponsor_name"
                      value={form.sponsor_name}
                      onChange={(e) => handleInputChange('sponsor_name', e.target.value)}
                      placeholder="Nom de la personne"
                      className="h-9 text-sm"
                      required
                    />
                  </div>
                )}

                {form.communication_source === 'autre' && (
                  <div>
                    <Label htmlFor="source_other" className="text-xs">Précisez *</Label>
                    <Input
                      id="source_other"
                      value={form.communication_source_other}
                      onChange={(e) => handleInputChange('communication_source_other', e.target.value)}
                      placeholder="Comment nous avez-vous connu ?"
                      className="h-9 text-sm"
                      required
                    />
                  </div>
                )}
                
                {/* Commentaire */}
                <div>
                  <Label htmlFor="comment" className="text-xs">Commentaire (optionnel)</Label>
                  <Textarea
                    id="comment"
                    value={form.comment}
                    onChange={(e) => handleInputChange('comment', e.target.value)}
                    placeholder="Une question ?"
                    rows={2}
                    className="text-sm resize-none"
                  />
                </div>
                
                {/* Submit */}
                <Button 
                  type="submit" 
                  className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 text-sm font-medium"
                  disabled={submitting}
                >
                  {submitting ? 'Inscription...' : 'S\'inscrire'}
                </Button>
                
                <p className="text-[10px] text-neutral-400 text-center leading-tight">
                  En vous inscrivant, vous acceptez de recevoir des communications concernant cette session.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Liste des sessions
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white py-4 px-3">
      <div className="max-w-3xl mx-auto">
        {/* Header avec logo */}
        <div className="text-center mb-4">
          {/* Logo du portail ou emoji par défaut */}
          {settingsLoading ? (
            <div className="inline-flex items-center justify-center w-16 h-16 bg-neutral-200 rounded-full mb-2 animate-pulse" />
          ) : portalSettings?.portal_logo?.data ? (
            <img 
              src={`data:${portalSettings.portal_logo.content_type};base64,${portalSettings.portal_logo.data}`}
              alt="Logo"
              className="mx-auto h-16 w-auto max-w-[160px] object-contain mb-2"
              data-testid="portes-ouvertes-logo"
            />
          ) : (
            <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-full mb-2">
              <span className="text-2xl">🏌️</span>
            </div>
          )}
          <h1 className="text-lg font-bold text-neutral-900">{golfInfo.golf_name}</h1>
          <h2 className="text-sm text-emerald-700 font-medium">Portes Ouvertes</h2>
          <p className="text-xs text-neutral-600 mt-1 max-w-sm mx-auto">
            Découvrez le golf gratuitement. Aucune expérience requise, matériel fourni !
          </p>
        </div>
        
        {/* Sessions */}
        <div className="space-y-3">
          {/* Toggle Vue */}
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-sm text-neutral-700">
              {sessions.length} session{sessions.length > 1 ? 's' : ''}
            </h3>
            <div className="flex gap-0.5 bg-neutral-100 p-0.5 rounded-lg">
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
                  viewMode === 'calendar' ? 'bg-white shadow text-emerald-700' : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <CalendarDays size={12} className="inline mr-0.5" />
                Mois
              </button>
              <button
                onClick={() => setViewMode('day')}
                className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
                  viewMode === 'day' ? 'bg-white shadow text-emerald-700' : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <Clock size={12} className="inline mr-0.5" />
                Jour
              </button>
            </div>
          </div>
          
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-lg p-3 animate-pulse">
                  <div className="h-4 bg-neutral-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-neutral-100 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : sessions.length === 0 ? (
            <Card className="text-center py-6">
              <CardContent>
                <Calendar className="w-10 h-10 text-neutral-300 mx-auto mb-2" />
                <p className="text-sm text-neutral-600">Aucune session disponible</p>
                <p className="text-xs text-neutral-500 mt-1">Revenez bientôt !</p>
              </CardContent>
            </Card>
          ) : viewMode === 'calendar' ? (
            /* Vue Calendrier - Optimisée */
            <Card>
              <CardContent className="p-2">
                {/* Navigation du mois */}
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1))}
                    className="p-1.5 hover:bg-neutral-100 rounded-lg transition-colors"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <h4 className="font-semibold text-sm capitalize">
                    {format(currentMonth, 'MMMM yyyy', { locale: fr })}
                  </h4>
                  <button
                    onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1))}
                    className="p-1.5 hover:bg-neutral-100 rounded-lg transition-colors"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
                
                {/* Jours de la semaine */}
                <div className="grid grid-cols-7 gap-0.5 mb-1">
                  {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, i) => (
                    <div key={i} className="text-center text-[10px] font-medium text-neutral-400 py-1">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Grille du calendrier */}
                <div className="grid grid-cols-7 gap-0.5">
                  {calendarDays.map((day, idx) => {
                    const dateKey = format(day, 'yyyy-MM-dd');
                    const daySessions = sessionsByDate[dateKey] || [];
                    const isCurrentMonth = isSameMonth(day, currentMonth);
                    const hasAvailable = daySessions.some(s => !s.is_full);
                    const totalPlaces = daySessions.reduce((sum, s) => sum + (s.places_remaining || 0), 0);
                    
                    return (
                      <div
                        key={idx}
                        className={`min-h-[44px] p-0.5 rounded border transition-all ${
                          !isCurrentMonth ? 'bg-neutral-50 opacity-40' :
                          daySessions.length > 0 && hasAvailable
                            ? 'bg-emerald-50 border-emerald-200 cursor-pointer hover:border-emerald-400'
                            : daySessions.length > 0
                            ? 'bg-neutral-100 border-neutral-200'
                            : 'border-transparent'
                        }`}
                        onClick={() => {
                          if (daySessions.length === 1 && hasAvailable) {
                            handleSelectSession(daySessions[0]);
                          }
                        }}
                      >
                        <div className={`text-[10px] font-medium ${
                          !isCurrentMonth ? 'text-neutral-300' : 
                          daySessions.length > 0 ? 'text-emerald-700' : 'text-neutral-500'
                        }`}>
                          {format(day, 'd')}
                        </div>
                        
                        {daySessions.length > 0 && isCurrentMonth && (
                          <div className="space-y-0.5 max-h-8 overflow-hidden">
                            {daySessions.slice(0, 2).map((session, sIdx) => (
                              <button
                                key={sIdx}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!session.is_full) handleSelectSession(session);
                                }}
                                disabled={session.is_full}
                                className={`w-full text-[9px] px-0.5 py-0 rounded truncate text-left leading-tight ${
                                  session.is_full 
                                    ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed' 
                                    : 'bg-emerald-500 text-white hover:bg-emerald-600'
                                }`}
                              >
                                {formatSessionTime(session.datetime)}-{formatSessionEndTime(session.datetime, session.duration_minutes)}
                              </button>
                            ))}
                            {daySessions.length > 2 && (
                              <span className="text-[8px] text-emerald-600">+{daySessions.length - 2}</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Légende compacte */}
                <div className="flex items-center gap-3 mt-2 pt-2 border-t text-[10px] text-neutral-500">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded bg-emerald-500"></div>
                    <span>Disponible</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded bg-neutral-200"></div>
                    <span>Complet</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Vue Jour */
            <div className="space-y-3">
              {/* Titre du mois */}
              <div className="text-center">
                <h4 className="text-sm font-medium text-neutral-700 capitalize">
                  {format(currentDate, 'MMMM yyyy', { locale: fr })}
                </h4>
              </div>
              
              {/* Sélecteur de jour scrollable */}
              <div className="overflow-x-auto pb-1">
                <div className="flex gap-1.5" style={{ width: 'max-content' }}>
                  {scrollDays.map(day => {
                    const dateKey = format(day, 'yyyy-MM-dd');
                    const daySessions = sessionsByDate[dateKey] || [];
                    const isSelected = isSameDay(day, currentDate);
                    const hasSessions = daySessions.length > 0;
                    const hasAvailable = daySessions.some(s => !s.is_full);
                    const isNewMonth = day.getDate() === 1;
                    
                    return (
                      <button
                        key={day.toISOString()}
                        onClick={() => setCurrentDate(day)}
                        className={`flex-shrink-0 w-12 py-1.5 rounded-lg text-center transition-all ${
                          isSelected 
                            ? 'bg-emerald-600 text-white shadow-md' 
                            : hasSessions && hasAvailable
                            ? 'bg-emerald-50 hover:bg-emerald-100 border border-emerald-200'
                            : 'bg-neutral-100 hover:bg-neutral-200'
                        }`}
                      >
                        <div className="text-[10px] uppercase opacity-70">
                          {isNewMonth ? format(day, 'MMM', { locale: fr }) : format(day, 'EEE', { locale: fr })}
                        </div>
                        <div className="text-base font-semibold">{format(day, 'd')}</div>
                        {hasSessions && !isSelected && (
                          <div className={`w-1 h-1 mx-auto mt-0.5 rounded-full ${hasAvailable ? 'bg-emerald-500' : 'bg-neutral-400'}`} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Sessions du jour sélectionné */}
              <div className="space-y-2">
                {currentDaySessions.length === 0 ? (
                  <div className="text-center py-8 bg-white rounded-lg border border-dashed">
                    <CalendarDays className="w-10 h-10 text-neutral-300 mx-auto mb-2" />
                    <p className="text-sm text-neutral-500">Aucune session ce jour</p>
                  </div>
                ) : (
                  currentDaySessions.map(session => {
                    const sessionTime = formatSessionTime(session.datetime);
                    const endTime = session.duration_minutes 
                      ? format(new Date(new Date(session.datetime).getTime() + session.duration_minutes * 60000), 'HH:mm')
                      : null;
                    
                    return (
                      <button
                        key={session.id}
                        onClick={() => handleSelectSession(session)}
                        disabled={session.is_full}
                        className={`w-full text-left rounded-lg overflow-hidden transition-all ${
                          session.is_full
                            ? 'opacity-60 cursor-not-allowed'
                            : 'hover:shadow-md active:scale-[0.99]'
                        }`}
                        style={{ 
                          backgroundColor: session.is_full ? '#f5f5f5' : '#ecfdf515',
                          borderLeft: `4px solid ${session.is_full ? '#d4d4d4' : '#10b981'}`
                        }}
                      >
                        <div className="px-3 py-2.5">
                          {/* Ligne 1: Nom + Badge */}
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-neutral-900 text-sm">
                              Portes Ouvertes
                            </h3>
                            {session.is_full ? (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">
                                Complet
                              </span>
                            ) : (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                                {session.places_remaining} place{session.places_remaining > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          
                          {/* Ligne 2: Horaires */}
                          <p className="text-xs text-neutral-600 mt-0.5">
                            {sessionTime}{endTime ? ` – ${endTime}` : ''}
                          </p>
                          
                          {/* Ligne 3: Pro */}
                          <div className="flex items-center justify-between mt-1">
                            {session.employee ? (
                              <p className="text-xs text-neutral-500">
                                {session.employee.first_name} {session.employee.last_name}
                              </p>
                            ) : (
                              <span />
                            )}
                            
                            {!session.is_full && (
                              <span className="text-xs text-emerald-600 font-medium flex items-center gap-0.5">
                                S'inscrire
                                <ChevronRight size={12} />
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Info */}
        <div className="mt-8 bg-white rounded-lg p-4 border">
          <h4 className="font-medium text-neutral-700 mb-2">ℹ️ Informations pratiques</h4>
          {golfInfo.info_pratiques ? (
            <div className="text-sm text-neutral-600 whitespace-pre-line">
              {golfInfo.info_pratiques}
            </div>
          ) : (
            <ul className="text-sm text-neutral-600 space-y-1">
              <li>• Gratuit et sans engagement</li>
              <li>• Matériel fourni sur place</li>
              <li>• Tenue décontractée acceptée (chaussures plates recommandées)</li>
              <li>• Une seule inscription par personne</li>
            </ul>
          )}
          {golfInfo.golf_address && (
            <p className="text-sm text-neutral-600 mt-3 flex items-center gap-1">
              <MapPin size={14} />
              {golfInfo.golf_address}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortesOuvertes;
